import { brand, caution, neutral, positive, urgent, white, black, plexusColors } from './color';

// Breakpoints (screen widths) - Following Plexus responsive design guidelines
export const breakpoints = {
  xs: '480px',   // Extra small devices
  sm: '576px',   // Small devices
  md: '768px',   // Medium devices
  lg: '1024px',  // Large devices  
  xl: '1200px',  // Extra large devices
  xxl: '1400px', // Extra extra large devices
};

// Plexus Design System Color Tokens
export const plexusTokens = {
  // Primary Plexus color families
  brand,
  caution, 
  neutral,
  positive,
  urgent,
  
  // Base colors
  white,
  black,
  
  // Complete color object
  colors: plexusColors,
};

// Semantic Color Mappings (using Plexus tokens)
export const semanticColors = {
  // Primary colors (brand)
  primary: brand[400],
  primaryHover: brand[500], 
  primaryActive: brand[300],
  primaryText: brand[100],
  
  // Secondary colors (neutral)
  secondary: neutral[500],
  secondaryHover: neutral[400],
  secondaryActive: neutral[300],
  
  // Success colors (positive)
  success: positive[400],
  successHover: positive[500],
  successActive: positive[300], 
  successLight: positive[800],
  successText: positive[100],
  
  // Warning colors (caution)
  warning: caution[400],
  warningHover: caution[500],
  warningActive: caution[300],
  warningLight: caution[800], 
  warningText: caution[100],
  
  // Error colors (urgent)
  error: urgent[400],
  errorHover: urgent[500],
  errorActive: urgent[300],
  errorLight: urgent[800],
  errorText: urgent[100],
  
  // Surface colors
  surface: white,
  surfaceSecondary: neutral[900],
  surfaceTertiary: neutral[800],
  background: neutral[900],
  backgroundSubtle: '#F8F9FA',
  
  // Border colors
  border: neutral[600],
  borderLight: neutral[700],
  borderDark: neutral[500],
  borderFocus: brand[400],
  
  // Text colors
  text: neutral[100],
  textSecondary: neutral[200], 
  textMuted: neutral[400],
  textInverse: white,
  textDisabled: neutral[500],
  
  // Interactive colors
  link: brand[400],
  linkHover: brand[300],
  linkVisited: brand[200],
};

// Legacy color mapping for backward compatibility
export const backgroundColor = {
  disabled: neutral[900],
};

export const color = {
  // Legacy mappings preserved for existing components
  black: black,
  brand: brand[400],
  brandDark: brand[300],
  brandLight: brand[800],
  brandLightest: brand[900],
  caution: caution[400],
  cautionDark: caution[300],
  cautionLight: caution[800],
  cautionLightest: caution[900],
  feedbackError: urgent[400],
  feedbackErrorLight: urgent[900],
  feedbackSuccess: positive[400],
  feedbackSuccessLight: positive[900],
  feedbackWarning: caution[400],
  neutral: neutral[400],
  neutralBackgroundGrey: neutral[900],
  neutralDarkGrey: neutral[100],
  neutralDeeperGrey: neutral[200],
  neutralLightGrey: neutral[700],
  neutralMidGrey: neutral[500],
  neutralShade: {
    100: neutral[100],
    200: neutral[200],
    300: neutral[300],
    400: neutral[400],
    500: neutral[500],
    600: neutral[600],
    700: neutral[700],
    800: neutral[800],
    900: neutral[900],
  },
  // Updated to use Plexus brand token instead of legacy blue
  primary: brand[400],
  primaryDark: brand[300],
  primaryDarker: brand[200],
  primaryLight: brand[800],
  positive: positive[400],
  positiveDark: positive[300],
  positiveLight: positive[800],
  postiveLightest: positive[900], // Note: keeping typo for backward compatibility
  secondaryPurple: '#834fd0', // Legacy color preserved
  urgent: urgent[400],
  urgentDark: urgent[300],
  urgentLight: urgent[800],
  urgentLightest: urgent[900],
  white: white,
};

export const formColor = {
  active: neutral[100],
  border: neutral[600],
  placeholder: neutral[400],
  invalid: urgent[400],
  inputBg: white,
  focus: brand[400],
  disabled: neutral[500],
};

export const focusState = {
  boxShadow: `${white} 0px 0px 0px 1px, ${brand[700]} 0px 0px 0px 4px`,
  outline: 0,
};

// Typography using Plexus design tokens
export const fontColor = {
  default: neutral[100],
  disabled: neutral[400],
  link: brand[400],
  linkHover: brand[300],
  white: white,
  muted: neutral[400],
  secondary: neutral[200],
  inverse: white,
};

export const formLineHeight = '18px';

// Plexus Spacing System (4px grid)
export const spaceUnit = 4;

export const spacing = {
  xs: spaceUnit,        // 4px
  sm: spaceUnit * 2,    // 8px  
  md: spaceUnit * 4,    // 16px
  lg: spaceUnit * 6,    // 24px
  xl: spaceUnit * 8,    // 32px
  xxl: spaceUnit * 10,  // 40px
};

// Plexus spacing aliases matching CSS variable naming
export const plexusSpacing = {
  'space-xs': '4px',
  'space-sm': '8px',
  'space-md': '16px', 
  'space-lg': '24px',
  'space-xl': '32px',
  'space-xxl': '40px',
};

// Typography Scale - Following Plexus design guidelines
export const typography = {
  fontFamily: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Border Radius - Plexus design system values
export const borderRadius = {
  sm: '4px',
  md: '6px', 
  lg: '8px',
  xl: '12px',
  full: '9999px',
};

// Shadow System - Plexus elevation tokens
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  bottom: '0 -2px 4px rgba(0, 0, 0, 0.1)',
};

// Z-Index System - Coordinated layer management
const INTERCOM_APP_LAUNCHER_Z_INDEX = 2147483003;
export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  
  // Legacy aliases
  menu: 1300,
  popoverLegacy: INTERCOM_APP_LAUNCHER_Z_INDEX + 2, // Renamed to avoid conflict
  richTextEditor: 13001,
  toast: INTERCOM_APP_LAUNCHER_Z_INDEX + 1,
};

// Comprehensive Plexus design system export
export const plexusDesignSystem = {
  colors: plexusTokens,
  semantic: semanticColors,
  spacing: plexusSpacing,
  typography,
  borderRadius,
  shadows,
  breakpoints,
  zIndex,
};

// Legacy styles object for backward compatibility
const styles = {
  breakpoints,
  color,
  fontColor,
  formColor,
  spacing,
  
  // New Plexus additions
  plexusTokens,
  semanticColors,
  typography,
  borderRadius,
  shadows,
  plexusSpacing,
  plexusDesignSystem,
};

export default styles;
